import { Fragment, memo, useEffect, useMemo, useState } from "react";

import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { isWidgetDisplayed } from "../../../../esri/widgets/expandUtils.js";
import { useViewPadding } from "../../../../hooks";
import {
  closeSidebar,
  openSidebar,
  toggleSidebar,
} from "../../../../redux/action/Sidebar-actions.js";
import ExportData from "./ExportData/ExportData.js";
import LanguageSelection from "./LanguageSelection/LanguageSelection.js";
import LayerList from "./LayerList/LayerList.js";
import PrintMap from "./PrtinMap/index.js";
import Search from "./Search/Search.js";
import SectionBasemap from "./SectionBasemap/SectionBasemap.js";
import SectionFilters from "./SectionFilters";
import SectionLogos from "./SectionLogos";
import {
  SidebarBody,
  SidebarContainer,
  StyledSection,
} from "./Sidebar-styled.js";
import SidebarHeader from "./SidebarHeader/index.js";
import Support from "./Support/index.js";
import UserAccount from "./UserAccount.js/index.js";

const sectionGroups = [
  ["Search"],
  ["LayerList", "BasemapGallery", "Filter"],
  ["Print", "FeatureTable"],
  ["LanguageSwitcher", "Support", "UserAccount"],
];

const Sidebar = ({ config }) => {
  const [expandSearch, setExpandSearch] = useState(false);

  const isSidebarOpen = useSelector((state) => state.sidebarReducer.open);
  const view = useSelector((state) => state.mapViewReducer.view);
  const printOpen = useSelector((state) => state.printWidget.open);
  const printLoading = useSelector((state) => state.printWidget.isLoading);
  const isOnboardingActive = useSelector(
    (state) => state.onboardingReducer.active
  );
  const { t } = useTranslation("common");

  const dispatch = useDispatch();
  const { animatePadding } = useViewPadding("left");

  useEffect(() => {
    if (!view) return;
    animatePadding(50);
  }, [view]);

  const handleToggleSidebar = () => {
    dispatch(toggleSidebar());
    if (expandSearch && expanded) {
      setExpandSearch(false);
    }
  };

  const setExpanded = (value) => {
    if (value) {
      dispatch(openSidebar());
    } else {
      dispatch(closeSidebar());
    }
  };
  const [animate, setAnimate] = useState(false);
  const widgetsToShow = useMemo(() => {
    // Flatten sectionGroups and filter by config
    return sectionGroups
      .flat()
      .filter((widgetName) => isWidgetDisplayed(config, widgetName));
  }, [config]);

  const expanded = useMemo(() => {
    if (printOpen && !printLoading) return false;
    if (isOnboardingActive) return false;
    // if (printOpen && printLoading) return isSidebarOpen;
    return isSidebarOpen;
  }, [isSidebarOpen, printOpen, printLoading, isOnboardingActive]);

  if (!view) return null;

  const widgetComponentMap = {
    Search: (
      <Search
        expanded={expanded}
        openSearch={() => {
          if (!expanded) setExpanded(true);
          setExpandSearch(true);
        }}
        setOpen={setExpandSearch}
        open={expandSearch}
        config={config}
      />
    ),
    LayerList: <LayerList expanded={expanded} />,
    BasemapGallery: <SectionBasemap expanded={expanded} />,
    Filter: <SectionFilters expanded={expanded} />,
    Print: <PrintMap expanded={expanded} />,
    FeatureTable: <ExportData expanded={expanded} />,
    LanguageSwitcher: <LanguageSelection expanded={expanded} />,
    Support: <Support expanded={expanded} />,
    UserAccount: <UserAccount expanded={expanded} />,
  };

  return (
    <SidebarContainer
      onClick={() => {
        setAnimate(true);
        setTimeout(() => {
          setAnimate(false);
        }, 300);
      }}
      animate={animate}
      expanded={expanded}
    >
      <SidebarHeader expanded={expanded} toggleSidebar={handleToggleSidebar} />
      <SidebarBody>
        {sectionGroups.slice(0, -1).map((group, i) => (
          <StyledSection key={i} hide={i > 0 && expandSearch}>
            {group
              .filter((widget) => widgetsToShow.includes(widget))
              .map((widget) =>
                widgetComponentMap[widget] ? (
                  <Fragment key={widget}>{widgetComponentMap[widget]}</Fragment>
                ) : null
              )}
          </StyledSection>
        ))}
        <StyledSection hide={sectionGroups.length - 1 > 0 && expandSearch}>
          {widgetsToShow.includes("LanguageSwitcher") &&
            widgetComponentMap["LanguageSwitcher"]}
          {widgetComponentMap["Support"]}
          {widgetComponentMap["UserAccount"]}
        </StyledSection>
      </SidebarBody>
      {/* {expanded && (
        <SidebarVersionContainer>
          <span>
            {t("screen.information.version")}: {process.env.REACT_APP_VERSION}|
            Powered by the Logistics Cluster © 2025
          </span>
        </SidebarVersionContainer>
      )} */}
      <SectionLogos expanded={expanded} />
    </SidebarContainer>
  );
};

export default memo(Sidebar);
